import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Pagination from './Pagination';
import classes from "./PostList.module.scss";
import { PageTitle } from "../PageTitle/PageTitle";

interface Post {
  id: number;
  title: { rendered: string };
  excerpt: { rendered: string };
  link: string;
  featured_media_url?: string;
}

const NewsList: React.FC<{ categoryId?: number; tagSlug?: string; limit: number }> = ({ categoryId, tagSlug, limit }) => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const location = useLocation();
  const pageTitle = getPageTitle(location.pathname);
  const langCode = location.pathname.split('/')[1] || 'ja'; // URLから言語コードを取得 (デフォルトは 'ja')

  // 言語に応じたタグスラッグを取得
  const getTagSlugByLang = () => {
    return 'education';
  };

  // tagSlugが指定されていない場合は、言語に応じたデフォルトタグを使用
  const effectiveTagSlug = tagSlug || getTagSlugByLang();

  const stripHtml = (html: string) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const limitText = (text: string, limit: number) => {
    const plainText = stripHtml(text);
    return plainText.length > limit ? plainText.substring(0, limit) + "..." : plainText;
  };

  useEffect(() => {
    const fetchPosts = async () => {
      // カテゴリ取得（研究成果）の場合は常に日本語APIを使用し、ja同様の記事を取得
      // タグ取得（NEWS）の場合は言語に応じたベースURLを使用
      const useJapaneseApi = !!categoryId;
      const baseUrl = (langCode === 'en' && !useJapaneseApi)
        ? 'https://hado-official.com/en/wp-json/wp/v2'
        : 'https://hado-official.com/wp-json/wp/v2';
      
      let url = `${baseUrl}/posts?_embed&per_page=${limit}&page=${currentPage}`;
      
      // カテゴリIDが指定されている場合はカテゴリ（Edu_研究成果 など）で取得
      // そうでなければタグ（education）で取得
      if (categoryId) {
        // カテゴリ「Edu_研究成果」（ID: 121）で取得
        url = `${baseUrl}/posts?_embed&categories=${categoryId}&per_page=${limit}&page=${currentPage}`;
      } else if (effectiveTagSlug) {
        // タグスラッグからタグIDを取得してニュースを取得
        const tagResponse = await fetch(`${baseUrl}/tags?slug=${effectiveTagSlug}`);
        const tagData = await tagResponse.json();
        
        if (tagData && tagData.length > 0) {
          const tagId = tagData[0].id;
          url = `${baseUrl}/posts?_embed&tags=${tagId}&per_page=${limit}&page=${currentPage}`;
        }
      }
      
      const response = await fetch(url);
      const total = response.headers.get('X-WP-Total') || '0';

      const data = await response.json();

      // デバッグ: 取得したデータを確認
      console.log('Fetched posts:', data.length);
      console.log('Lang code:', langCode);
      console.log('Base URL:', baseUrl);
      if (data.length > 0) {
        console.log('Sample post links:', data.slice(0, 5).map((p: any) => p.link));
      }

      // 言語に応じて記事をフィルタリング
      // 研究成果ページ（categoryId）の場合はja同様に日本語記事を表示
      let filteredPosts;
      const filterAsJapanese = langCode === 'ja' || !!categoryId;
      if (filterAsJapanese) {
        // 日本語記事：/en/を含まず、/zh/も含まない記事
        filteredPosts = data.filter((post: any) => {
          const postLink = post.link || '';
          return !postLink.includes('/en/') && !postLink.includes('/zh/');
        });
      } else {
        // 英語版：取得した記事のリンクに/en/が含まれているか確認
        filteredPosts = data.filter((post: any) => {
          const postLink = post.link || '';
          return postLink.includes('/en/');
        });
      }

      console.log('Filtered posts:', filteredPosts.length);

      // フィルタリング後の総数を再計算
      setTotalPosts(filteredPosts.length);

      setPosts(filteredPosts.map((post: any) => ({
        ...post,
        featured_media_url: post._embedded?.['wp:featuredmedia']?.[0]?.source_url,
      })));
    };

    document.title = pageTitle;
    fetchPosts().catch(err => console.error('Error fetching data:', err));
  }, [categoryId, effectiveTagSlug, currentPage, limit, pageTitle, langCode]);

  return (
    <div className={classes.root + ' contents'}>
      <PageTitle className={classes.pageTitle} title={pageTitle} />
      <ul className={classes.newsList + ' contentWidth'}>
        {posts.map((post, index) => (
          <li key={index} className={classes.newsItem}>
            <Link to={`/${langCode}/post/${post.id}`}>
              {post.featured_media_url && <img src={post.featured_media_url} alt={post.title.rendered} className={classes.newsItemImg} />}
              <h2 className={classes.newsItemTitle}>{limitText(post.title.rendered, 30)}</h2>
              <div className={classes.newsItemText}>{limitText(post.excerpt.rendered, 50)}</div>
            </Link>
          </li>
        ))}
      </ul>
      <Pagination totalPosts={totalPosts} postsPerPage={limit} currentPage={currentPage} setCurrentPage={setCurrentPage} />
    </div>
  );
};

const getPageTitle = (path: string) => {
  switch (path) {
    case '/ja/news': return 'NEWS';
    case '/en/news': return 'NEWS';
    case '/ja/interview': return '導入校インタビュー';
    case '/en/interview': return 'School Interviews';
    case '/ja/research-result': return '研究成果・発表';
    case '/en/research-result': return 'Research Results';
    default: return '';
  }
};

export default NewsList;
